import React, { ChangeEvent, KeyboardEvent, useEffect, useRef, useState, } from "react";
import { Box, Flex, HStack, Show, Spacer, Text, useMediaQuery, VStack, } from "@chakra-ui/react";
import { useSettingsContext } from "../../../contexts/SettingsContext";
import APButton from "../../../components/APButton";
import NavTopBar from "../../../components/NavTopBar";
import APAudioPlayer from "../../../components/APAudioPlayer";
import APLink from "../../../components/APLink";
import APProgressBar from "../../../components/APProgressBar";
import { ErrorTransModel } from "ap-exerciser-sdk/models";
import { AlloprofLogo, Checkmark, Close, Cross, UnsureTeen, Wallpaper } from "../../../components/APAssetAtlas";
import { VocabWord } from "../../../VocabModel";
import APExerciseField from "./APExerciseField";
import { useVocabIntl } from "../../../hooks/useVocabTranslation";

interface ExerciseScreenProps {
  onValidate: Function;
  words: Array<VocabWord>;
  name: String;
  scenarioId: string;
  step: number;
  currentStep: number;
  setExitExercise: Function;
  audioPath: string;
  progress: number;
  correct: number;
  wordValue: string;
  setWordValue: Function;
  error: ErrorTransModel | null;
  setError: (value: ErrorTransModel | null) => void;
  onPass: (wordItem: VocabWord) => void;
  initialWordsLength: number;
  success: boolean;
  warning: boolean;
  fail: boolean;
  pass: boolean;
}

function ExerciseScreen(props: ExerciseScreenProps) {
  const {
    onValidate,
    words,
    name,
    scenarioId,
    step,
    currentStep,
    setExitExercise,
    audioPath,
    progress,
    correct,
    wordValue,
    setWordValue,
    error,
    setError,
    onPass,
    initialWordsLength,
    success,
    warning,
    fail,
    pass,
  } = props;

  const { getFormattedString, APFormattedMessage } = useVocabIntl();
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const [lock, setLock] = useState(false);
  const [inputValue, setInputValue] = useState(wordValue || "");
  const inputField = useRef<HTMLInputElement>(null!);

  const { setSettingModalOpen, setProfileOpen, skin, setSettingsTabIndex } = useSettingsContext();
  const handleOpenSettings = () => {
    setSettingModalOpen(true);
  };

  const errorMsg = error ?
    getFormattedString(error.id)
    : "";

  const onKeyDownCallback = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || !inputField.current.value || lock) return;
    event.stopPropagation();
    event.preventDefault();
    onValidateCallback();
  };

  const onValidateCallback = () => {
    inputField.current.blur();
    onValidate(words, scenarioId, false, inputField.current.value);
  }

  const onChangeCallback = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setError(null);
    setLock(false);
  }


  useEffect(() => {
    if (inputField.current) {
      inputField.current.value = "";
      setTimeout(() => inputField.current.focus(), 100);
    }
  }, [step]);

  return (
    <Flex layerStyle={"pageContainer"} zIndex={10}>
      <NavTopBar
        backIcon={<Close />}
        handleBack={() => setExitExercise(true)}
        title={
          <Text fontSize={["md", "2xl"]} fontWeight={"bold"} textAlign={"center"} layerStyle={"textGlow"}>
            {(name && name !== "") ? (name) : (
              <APFormattedMessage id={"word-list-name"} />
            )}
          </Text>
        }
        openProfile={() => setProfileOpen(true)}
        openSettings={() => handleOpenSettings()}
      />
      <VStack key={"exercise-content"} layerStyle={"pageContent"} maxWidth={"container.sm"}
              height={isLargeScreen ? "md" : "xs"}>
        <HStack alignItems={"center"} gap={2} width={"100%"}>
          <APProgressBar flexGrow={1} value={progress} minWidth={0} />
          <Text fontSize={"sm"} color="#000" fontWeight="500">
            Mot {currentStep} sur {initialWordsLength}
          </Text>
        </HStack>
        <VStack flexGrow={1}
                justifyContent={"space-evenly"}
                alignItems={"center"}
                padding={isLargeScreen ? "0 2rem" : "0"}>
          <Box fontSize={["lg", null, "2xl"]} textAlign={"center"} fontWeight={"bold"}>
            <APFormattedMessage id={"write-word-you-hear"} />
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent={"center"}
          >
            <APAudioPlayer
              key={"ExerciseAudioPlayer"}
              autoPronounce={true}
              file={audioPath}
              tabIndex={4}
            />
          </Box>
          <Spacer flex={"0 2 5%"} />
          <Box position={"relative"} width={"90%"} maxWidth={["sm"]}>
            <Flex position={"relative"} width={"100%"} height={"fit-content"}>
              <APExerciseField
                ref={inputField}
                maxLength={Math.max(32, words[step - 1]?.word?.length ?? 0)}
                isInvalid={!!error}
                error={errorMsg}
                borderColor={(fail || warning) ? "brightOrange.500" : success ? "brightGreen" : undefined}
                onChange={onChangeCallback}
                onKeyDown={onKeyDownCallback}
              />
              <Box position={"absolute"} fontSize={"md"} right={"0.5rem"} top={"50%"}
                   transform={"translate(-50%,-50%)"}>
                <Checkmark display={success ? "block" : "none"} color={"success"} />
                <Cross display={warning || fail ? "block" : "none"} color={"alert.fg"} />
              </Box>
            </Flex>
            <APLink
              sx={{ fontWeight: 500 }}
              marginTop={isLargeScreen ? "1" : "0"}
              onClick={() => {
                setWordValue(inputValue);
                onPass(words[step - 1])
              }}
              className="pass-btn"
              tabIndex={3}
              aria-label={getFormattedString('aria-label-pass-button')}
            >
              <APFormattedMessage id={"pass"} />
            </APLink>
          </Box>
        </VStack>
        <APButton
          sx={{
            position: "absolute",
            bottom: "0",
            left: "50%",
            transform: "translate(-50%, 50%)",
          }}
          disabled={!inputField.current || !inputField.current.value}
          variant={"raised"}
          colorScheme={"primary"}
          rounded={"full"}
          size={"lg"}
          onClick={onValidateCallback}
          tabIndex={2}
          aria-label={getFormattedString('aria-label-validate-button')}>
          <APFormattedMessage id={"validate"} />
        </APButton>
      </VStack>
      <Spacer flex={"0 2 10%"} />
      {!isLargeScreen &&
          <UnsureTeen position={"absolute"} right={"0"} height={"16rem"} preserveAspectRatio={"meet"} bottom={0}
                      zIndex={-1} width={"auto"} transform={"translate(40%, 0)"} />}
      <HStack justifyContent={{ base: "center", md: "space-between" }} layerStyle={"pageFooter"}>
        <Show above={"md"}>
          <APButton variant={"outline"} colorScheme={"tertiary"} size={"md"} fontSize={"sm"} gap={2}
                    onClick={() => {
                      setSettingsTabIndex(1)
                      setProfileOpen(true)
                    }}>
            <Wallpaper />
            <APFormattedMessage id={"wallpaper-button"} />
          </APButton>
        </Show>
        <AlloprofLogo height={"1.5rem"} margin={"0.25rem 1rem"} layerStyle={"textGlow"} />
      </HStack>
    </Flex>
  );
}

export default ExerciseScreen;
